/**
 * Speech Service
 * Provides text-to-speech functionality using Web Speech API
 */

let currentUtterance = null;
let isSpeaking = false;

/**
 * Check if browser supports Web Speech API
 * @returns {boolean}
 */
export function isSpeechSupported() {
  const SpeechSynthesisUtterance = window.SpeechSynthesisUtterance || window.webkitSpeechSynthesisUtterance;
  return !!SpeechSynthesisUtterance && !!window.speechSynthesis;
}

/**
 * Get available voices
 * @returns {Array} Array of voice objects
 */
export function getAvailableVoices() {
  if (!isSpeechSupported()) return [];
  return window.speechSynthesis.getVoices().map(voice => ({
    name: voice.name,
    lang: voice.lang,
    uri: voice.voiceURI,
  }));
}

/**
 * Speak text
 * @param {string} text - Text to speak
 * @param {Object} options - Speech options
 * @param {string} options.voiceName - Voice name (optional)
 * @param {number} options.rate - Speech rate 0.5-2.0 (default: 1.0)
 * @param {number} options.pitch - Pitch 0.1-2.0 (default: 1.0)
 * @param {number} options.volume - Volume 0-1.0 (default: 1.0)
 * @param {Function} options.onEnd - Callback when speech ends
 * @returns {Promise<void>}
 */
export function speak(text, options = {}) {
  return new Promise((resolve, reject) => {
    if (!isSpeechSupported()) {
      reject(new Error('Speech synthesis not supported in this browser'));
      return;
    }

    if (!text || !text.trim()) {
      reject(new Error('No text to speak'));
      return;
    }

    // Stop any ongoing speech
    stopSpeaking();

    const SpeechSynthesisUtterance = window.SpeechSynthesisUtterance || window.webkitSpeechSynthesisUtterance;
    currentUtterance = new SpeechSynthesisUtterance(text);

    // Set voice if specified
    if (options.voiceName) {
      const voices = getAvailableVoices();
      const selectedVoice = voices.find(v => v.name === options.voiceName);
      if (selectedVoice) {
        const allVoices = window.speechSynthesis.getVoices();
        const voice = allVoices.find(v => v.name === selectedVoice.name);
        if (voice) currentUtterance.voice = voice;
      }
    }

    // Set speech parameters
    currentUtterance.rate = Math.max(0.5, Math.min(2.0, options.rate ?? 1.0));
    currentUtterance.pitch = Math.max(0.1, Math.min(2.0, options.pitch ?? 1.0));
    currentUtterance.volume = Math.max(0, Math.min(1.0, options.volume ?? 1.0));

    // Handle speech end
    currentUtterance.onend = () => {
      isSpeaking = false;
      if (options.onEnd) options.onEnd();
      resolve();
    };

    currentUtterance.onerror = (error) => {
      isSpeaking = false;
      reject(error);
    };

    isSpeaking = true;
    window.speechSynthesis.speak(currentUtterance);
  });
}

/**
 * Stop speaking
 */
export function stopSpeaking() {
  if (window.speechSynthesis && isSpeaking) {
    window.speechSynthesis.cancel();
    isSpeaking = false;
  }
}

/**
 * Check if currently speaking
 * @returns {boolean}
 */
export function isSpeaking_() {
  return isSpeaking;
}

/**
 * Extract text from DOM element
 * @param {HTMLElement} element - DOM element
 * @returns {string} Extracted text
 */
export function extractText(element) {
  if (!element) return '';
  return element.innerText || element.textContent || '';
}

/**
 * Speak text from DOM element
 * @param {HTMLElement} element - DOM element to extract text from
 * @param {Object} options - Speech options
 * @returns {Promise<void>}
 */
export function speakElement(element, options = {}) {
  const text = extractText(element);
  return speak(text, options);
}